import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Come compare la risposta di MUNE (28/10/2026, sera). Lorenzo: «l'unica cosa che non mi fa
 * impazzire è come spunta il testo, sembra un po' scattante, hai altre proposte?». Prima si
 * scriveva solo a scatti: otto lettere 25 volte al secondo, come i dialoghi dei giochi a 8 bit.
 * Adesso le maniere sono quattro, a confronto nella galleria /mascotte; la chat usa MODO_RIVELA.
 *
 * In tutte il testo intero è già al suo posto e non si vede: le righe non saltano mentre
 * compare. Nelle tre nuove la mascotte salta fuori a risposta finita, dopo l'ultima parola;
 * a scatti viene spinta avanti mentre parla, come prima.
 */
export type ModoRivela = 'parole' | 'frasi' | 'insieme' | 'scatti';

/** La maniera della chat. */
export const MODO_RIVELA: ModoRivela = 'parole';

export const MODI_RIVELA: Array<{ modo: ModoRivela; nome: string; come: string }> = [
  { modo: 'parole', nome: 'Parola per parola', come: "Le parole compaiono una dopo l'altra, sfumando dal basso. La mascotte salta fuori alla fine." },
  { modo: 'frasi', nome: 'Frase per frase', come: "Una frase alla volta, in dissolvenza, con una pausa breve tra l'una e l'altra." },
  { modo: 'insieme', nome: 'Tutta insieme', come: 'La risposta compare intera, in una dissolvenza di mezzo secondo.' },
  { modo: 'scatti', nome: 'A scatti, come prima', come: 'Lettere a gruppi, 25 volte al secondo, come i giochi a 8 bit. La mascotte viene spinta avanti mentre parla.' },
];

// ── Il testo: normale e grassetto ──────────────────────────────────────────

/** Un pezzo di risposta: testo normale o in grassetto. */
export interface Pezzo { testo: string; forte: boolean }

/**
 * Il grassetto di Markdown (**così**) diventa grassetto vero: Lorenzo, 28/10, «il
 * doppio asterisco non ci deve essere, deve essere sostituito sempre dal vero e proprio
 * grassetto». Gli asterischi doppi rimasti senza coppia si tolgono.
 */
export function pezziDi(testo: string): Pezzo[] {
  const pezzi: Pezzo[] = [];
  const normale = (t: string) => { const s = t.replace(/\*\*/g, ''); if (s) pezzi.push({ testo: s, forte: false }); };
  const re = /\*\*(?=\S)([\s\S]*?\S)\*\*/g;
  let ultimo = 0;
  for (let m = re.exec(testo); m; m = re.exec(testo)) {
    normale(testo.slice(ultimo, m.index));
    pezzi.push({ testo: m[1]!.replace(/\*\*/g, ''), forte: true });
    ultimo = m.index + m[0].length;
  }
  normale(testo.slice(ultimo));
  return pezzi;
}

/** Il testo che si legge, senza asterischi: per contare i caratteri e per i lettori di schermo. */
export const testoPiano = (testo: string) => pezziDi(testo).map((p) => p.testo).join('');

/** I pezzi tagliati al carattere `n` del testo che si legge: quelli scritti e il resto (a scatti). */
export function taglia(pezzi: Pezzo[], n: number): { scritti: Pezzo[]; resto: Pezzo[] } {
  const scritti: Pezzo[] = [];
  const resto: Pezzo[] = [];
  let pos = 0;
  for (const p of pezzi) {
    const qui = Math.min(Math.max(n - pos, 0), p.testo.length);
    if (qui > 0) scritti.push({ testo: p.testo.slice(0, qui), forte: p.forte });
    if (qui < p.testo.length) resto.push({ testo: p.testo.slice(qui), forte: p.forte });
    pos += p.testo.length;
  }
  return { scritti, resto };
}

// ── Le unità: quello che compare tutto insieme ──────────────────────────────

/** Una parola, una frase o la risposta intera, con lo spazio che la segue (che non sfuma). */
export interface Unita { pezzi: Pezzo[]; dopo: string }

/** Dove finisce una frase: punto, punto esclamativo o interrogativo, puntini, due punti. */
const FINE_FRASE = /[.!?…:;]["»)\]]*$/u;

/** Il testo diviso in unità, col grassetto di ogni pezzo (anche a cavallo di una parola: «**Graci**,»). */
export function unitaDi(pezzi: Pezzo[], modo: Exclude<ModoRivela, 'scatti'>): Unita[] {
  if (modo === 'insieme') return pezzi.length ? [{ pezzi, dopo: '' }] : [];
  const unita: Unita[] = [];
  let corrente: Pezzo[] = [];
  const metti = (testo: string, forte: boolean) => {
    const ultimo = corrente[corrente.length - 1];
    if (ultimo && ultimo.forte === forte) ultimo.testo += testo;
    else corrente.push({ testo, forte });
  };
  const chiudi = (dopo: string) => {
    if (corrente.length) unita.push({ pezzi: corrente, dopo });
    else if (unita.length) unita[unita.length - 1]!.dopo += dopo;
    else unita.push({ pezzi: [], dopo });
    corrente = [];
  };
  const gettoni = pezzi.flatMap((p) => p.testo.split(/(\s+)/).filter(Boolean).map((t) => ({ t, forte: p.forte })));
  gettoni.forEach(({ t, forte }, i) => {
    const spazio = /^\s+$/.test(t);
    if (modo === 'parole') {
      if (spazio) chiudi(t);
      else metti(t, forte);
      return;
    }
    // Frasi: lo spazio dentro la frase resta nella frase; quello dopo il punto, o un a capo, la chiude.
    if (spazio) {
      const prima = gettoni[i - 1]?.t ?? '';
      if (t.includes('\n') || FINE_FRASE.test(prima)) chiudi(t);
      else metti(t, forte);
      return;
    }
    metti(t, forte);
  });
  if (corrente.length) chiudi('');
  return unita;
}

/** Ogni quanti millisecondi compare un'unità. Per «parole» tutta la risposta sta tra 0,35 e 1,6 secondi. */
export function passoDi(modo: ModoRivela, unita: number, caratteri: number): number {
  switch (modo) {
    case 'parole':
      return Math.max(10, Math.min(70, Math.min(1600, Math.max(350, caratteri * 4)) / Math.max(1, unita)));
    case 'frasi':
      return Math.max(120, Math.min(320, 1800 / Math.max(1, unita)));
    case 'insieme':
      return 0;
    case 'scatti':
      return 40;
  }
}

/** A scatti: circa 200 caratteri al secondo, mai più di 2 secondi. */
const durataScatti = (caratteri: number) => Math.min(2000, Math.max(400, caratteri * 5));

/**
 * Quanti caratteri si vedono adesso, a scatti. Non taglia mai un'emoji a metà: una coppia di
 * surrogati spezzata si vedrebbe come un rombo col punto di domanda per uno scatto.
 */
function quantiCaratteri(testo: string, inizio: number | undefined): number {
  const tot = testo.length;
  if (inizio === undefined) return 0;
  const k = Math.min(1, Math.max(0, (performance.now() - inizio) / durataScatti(tot)));
  let n = Math.round(tot * k);
  const c = testo.charCodeAt(n - 1);
  if (n < tot && c >= 0xd800 && c <= 0xdbff) n += 1;
  return n;
}

/** Quante unità si vedono adesso. */
function quanteUnita(tot: number, passo: number, inizio: number | undefined): number {
  if (inizio === undefined) return 0;
  if (passo <= 0) return tot;
  return Math.min(tot, Math.floor((performance.now() - inizio) / passo) + 1);
}

/** Quando ha cominciato a comparire ogni risposta: se il pannello si rimonta a metà, riprende da lì. */
const inizioRivela = new Map<string, number>();

export const menoMovimento = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export interface Rivela {
  modo: ModoRivela;
  /** Le unità; a scatti è vuoto: si taglia sui caratteri. */
  unita: Unita[];
  /** Quante unità (a scatti: caratteri) si vedono adesso. */
  visibili: number;
  totale: number;
  /** Quelle già visibili quando il testo si è disegnato: non sfumano di nuovo. */
  giaAlMontaggio: number;
  /** Sta ancora comparendo. */
  scrive: boolean;
  /** Mostra subito tutto (un clic sulla risposta o sulla mascotte). */
  salta: () => void;
}

/**
 * La risposta che compare da sola. `id` tiene il punto a cui era arrivata se il componente si
 * smonta e rimonta (in sviluppo React.StrictMode lo fa subito): riparte da lì, senza lampi del
 * testo intero. `seSparito` dice, allo smontaggio, se la risposta non c'è più (nuova
 * conversazione): allora si dimentica anche quando era partita.
 */
export function useRivela(p: {
  id: string;
  pezzi: Pezzo[];
  modo: ModoRivela;
  attivo: boolean;
  onFine: () => void;
  seSparito?: () => boolean;
}): Rivela {
  const { id, pezzi, modo, attivo } = p;
  const piano = useMemo(() => pezzi.map((x) => x.testo).join(''), [pezzi]);
  const unita = useMemo(() => (modo === 'scatti' ? [] : unitaDi(pezzi, modo)), [pezzi, modo]);
  const totale = modo === 'scatti' ? piano.length : unita.length;
  const passo = passoDi(modo, unita.length, piano.length);
  const conta = useCallback(
    () => (modo === 'scatti' ? quantiCaratteri(piano, inizioRivela.get(id)) : quanteUnita(totale, passo, inizioRivela.get(id))),
    [modo, piano, id, totale, passo],
  );
  const [visibili, setVisibili] = useState(() => (attivo ? conta() : totale));
  const [giaAlMontaggio] = useState(() => (attivo ? conta() : totale));
  const onFine = useRef(p.onFine);
  onFine.current = p.onFine;
  const seSparito = useRef(p.seSparito);
  seSparito.current = p.seSparito;
  const salta = useRef<() => void>(() => {});

  useEffect(() => {
    if (!attivo) return;
    if (menoMovimento() || totale === 0) { setVisibili(totale); onFine.current(); return; }
    if (!inizioRivela.has(id)) inizioRivela.set(id, performance.now());
    let t = 0;
    const passa = () => {
      const n = conta();
      setVisibili(n);
      if (n < totale) return;
      window.clearInterval(t);
      inizioRivela.delete(id);
      onFine.current();
    };
    t = window.setInterval(passa, modo === 'scatti' ? 40 : Math.max(10, Math.min(40, passo || 40)));
    salta.current = () => { inizioRivela.set(id, -Infinity); passa(); };
    passa();
    return () => {
      window.clearInterval(t);
      if (seSparito.current?.()) inizioRivela.delete(id);
    };
  }, [attivo, id, totale, conta, modo, passo]);

  const visti = attivo ? visibili : totale;
  return { modo, unita, visibili: visti, totale, giaAlMontaggio, scrive: visti < totale, salta: () => salta.current() };
}
